// Related contacts in the agent app (2026-10-20; server/src/ai/related-contacts.js,
// docs/pipeline-behaviors.md "Related contacts"). Pure helpers, unit-tested in
// ui/test/relatedContacts.test.js.

// The Queue: a duplicate whose primary is a row on this page is folded under
// that primary, so four copies of one submission are one row with "×4".
// A duplicate whose primary is not on the page (filtered out, or a contact
// this person cannot see) stays its own row. Same-issue and different-issue
// contacts always stay separate rows. Returns [{ row, duplicates: [rows] }]
// in the page's order.
export function groupDuplicateRows(rows) {
  const list = rows ?? []
  const ids = new Set(list.map((r) => r.id))
  const dupsByPrimary = new Map()
  for (const r of list) {
    const p = r.relatedContacts?.duplicateOf ?? null
    if (p && p !== r.id && ids.has(p)) {
      if (!dupsByPrimary.has(p)) dupsByPrimary.set(p, [])
      dupsByPrimary.get(p).push(r)
    }
  }
  const folded = new Set([...dupsByPrimary.values()].flat().map((r) => r.id))
  return list.filter((r) => !folded.has(r.id)).map((row) => ({ row, duplicates: dupsByPrimary.get(row.id) ?? [] }))
}

// The send guard's client side: our most recent reply on a related contact
// (duplicate, same issue or already handled) within the server's window,
// { convNumber, minutesAgo } or null. The server checks again.
export function recentRelatedReply(related, now = new Date()) {
  const windowMin = related?.recentReplyGuardMinutes ?? 60
  let best = null
  for (const m of related?.members ?? []) {
    if (m.relation === 'different_issue' || !m.lastReplyAt) continue
    const at = new Date(m.lastReplyAt)
    const minutes = (now - at) / 60000
    if (Number.isNaN(minutes) || minutes < 0 || minutes > windowMin) continue
    if (!best || at > best.at) best = { at, convNumber: m.convNumber ?? null, minutesAgo: Math.max(0, Math.round(minutes)) }
  }
  return best ? { convNumber: best.convNumber, minutesAgo: best.minutesAgo } : null
}

export function recentReplyMessage({ convNumber, minutesAgo }) {
  return `We replied to this customer on #${convNumber ?? '?'} ${minutesAgo === 1 ? '1 minute' : `${minutesAgo} minutes`} ago. Send anyway?`
}

// "Use Corrected Address": the To field with the misspelled address replaced
// (case-insensitive), or the corrected address alone when the field does not
// hold it.
export function withCorrectedAddress(to, typo) {
  if (!typo?.suggested) return to ?? ''
  const current = String(to ?? '')
  const lower = current.toLowerCase()
  const at = lower.indexOf(String(typo.address ?? '').toLowerCase())
  if (!typo.address || at === -1) return current.trim() ? current : typo.suggested
  return current.slice(0, at) + typo.suggested + current.slice(at + typo.address.length)
}

function list(numbers) {
  return numbers.map((n) => `#${n ?? '?'}`).join(', ')
}

function aboutText(m) {
  if (m.erpOrderId) return `order ${m.erpOrderId}`
  if (m.intent) return m.intent.replace(/_/g, ' ')
  return 'another matter'
}

function dateText(t) {
  if (!t) return 'earlier'
  try { return new Date(t).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' }) } catch { return 'earlier' }
}

function dateTimeText(t) {
  if (!t) return ''
  try { return new Date(t).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' }).replace(',', '') } catch { return '' }
}

export const RELATION_LABELS = Object.freeze({
  duplicate: 'duplicate',
  same_issue_more_info: 'same issue',
  same_issue_handled: 'same issue, answered',
  different_issue: 'different issue',
})

// The "related" chip's list (Queue and contact header): each related contact
// with its status and relation, in contact-number order.
export function relatedChipEntries(members) {
  return [...(members ?? [])]
    .sort((a, b) => Number(a.convNumber ?? 0) - Number(b.convNumber ?? 0))
    .map((m) => ({ id: m.id, convNumber: m.convNumber, status: m.status, relation: RELATION_LABELS[m.relation] ?? m.relation }))
}

// What each close button closes (2026-10-20, button targets): the label
// names the contact(s) it closes, from the same list the click acts on, so the
// label and the action cannot disagree. Members the viewer may not open are
// not in the server's lists (and are dropped here too), so they are neither
// listed nor closed.
//   group     the primary's open duplicates or same-issue contacts: "Close
//             #a, #b", or past three "Close 4 Related (#a, #b, #c, …)"
//   handled   the whole open issue group, this primary first (2026-10-21):
//             "Close #a, #b – No Reply Needed", or past three "Close 4
//             Contacts (#a, #b, #c, …) – No Reply Needed"
//   self      this contact: "Close This Contact (#n) As Duplicate" / "As
//             Related"
// { label, convNumbers, ids }.
export function closeTarget(kind, { members = [], selfConvNumber = null } = {}) {
  const num = (n) => `#${n ?? '?'}`
  if (kind === 'handled') {
    const shown = [{ convNumber: selfConvNumber, id: null }, ...(members ?? []).filter((m) => m && m.canOpen !== false)]
    const nums = shown.map((m) => num(m.convNumber))
    const label = nums.length > 3 ? `Close ${nums.length} Contacts (${nums.slice(0, 3).join(', ')}, …) – No Reply Needed` : `Close ${nums.join(', ')} – No Reply Needed`
    return { label, convNumbers: shown.map((m) => m.convNumber ?? null), ids: shown.map((m) => m.id ?? null) }
  }
  if (kind === 'primary' || kind === 'sameIssue') {
    const shown = (members ?? []).filter((m) => m && m.canOpen !== false)
    const nums = shown.map((m) => num(m.convNumber))
    const noun = kind === 'primary' ? 'Duplicates' : 'Related'
    const label = nums.length > 3 ? `Close ${nums.length} ${noun} (${nums.slice(0, 3).join(', ')}, …)` : `Close ${nums.join(', ')}`
    return { label, convNumbers: shown.map((m) => m.convNumber ?? null), ids: shown.map((m) => m.id ?? null) }
  }
  const self = num(selfConvNumber)
  const suffix = kind === 'duplicate' ? ' As Duplicate' : ' As Related'
  return { label: `Close This Contact (${self})${suffix}`, convNumbers: [selfConvNumber ?? null], ids: [] }
}

// The banners for one contact, in order, from GET /conversations/:id's
// relatedContacts. Each: { kind, severity, text, links, closeLabel, ... };
// links maps a contact number in the text to the contact it opens, always
// in a CXHub contact tab (2026-10-21: no banner opens a browser tab). The
// component adds the buttons, with closeLabel, the same text the sentence
// names (closeTarget()). Agents close duplicates and same-issue contacts
// FIRST, then reply on the primary (2026-10-20, close first).
//
// The already-answered banner (2026-10-20, button targets; 2026-10-21, one
// verdict per issue group) is the primary's only: a duplicate or same-issue
// secondary shows none, its own banner is its only action. On the primary it
// offers "Close #a, #b – No Reply Needed" (offerClose), closing the whole
// open group, only when the server's group verdict says our earlier reply
// still answers the customer (answered.covered). When it does not, it says
// what changed and that a new reply is needed, with no close. With no check,
// it states the earlier answer and offers no close. Its number and its
// "Open #…" button open the answered contact in a CXHub tab (canOpen).
export function relatedBanners(related, { selfConvNumber = related?.selfConvNumber ?? null } = {}) {
  if (!related) return []
  const out = []
  const ref = (m) => ({ [String(m.convNumber)]: { id: m.id } })
  if (related.duplicateOf) {
    const p = related.duplicateOf
    const close = closeTarget('duplicate', { selfConvNumber })
    out.push({ kind: 'duplicate', severity: 'warning', primaryId: p.id, primaryConvNumber: p.convNumber, closeLabel: close.label, links: ref(p), text: `Duplicate of #${p.convNumber ?? '?'} (${p.status}, assigned to ${p.assignedTo ?? 'nobody'}). Handle it there.` })
  }
  if (related.secondaryOf) {
    const p = related.secondaryOf
    const close = closeTarget('secondary', { selfConvNumber })
    out.push({ kind: 'secondary', severity: 'warning', primaryId: p.id, primaryConvNumber: p.convNumber, closeLabel: close.label, links: ref(p), text: `Same issue as #${p.convNumber ?? '?'}, which has the reply that covers both. Select ${close.label} to close this one now.` })
  }
  const dups = (related.openDuplicates ?? []).filter((m) => m && m.canOpen !== false)
  if (related.isPrimary && dups.length) {
    const one = dups.length === 1
    const close = closeTarget('primary', { members: dups })
    out.push({ kind: 'primary', severity: 'info', count: dups.length, closeLabel: close.label, closeConvNumbers: close.convNumbers, links: Object.assign({}, ...dups.map(ref)), text: `${dups.length} duplicate${one ? '' : 's'} of this contact ${one ? 'is' : 'are'} in the queue (${list(dups.map((x) => x.convNumber))}). Select ${close.label} to close ${one ? 'it' : 'them'} now.` })
  }
  const same = (related.openSameIssue ?? []).filter((m) => m && m.canOpen !== false)
  if (related.isPrimary && same.length) {
    const one = same.length === 1
    const close = closeTarget('sameIssue', { members: same })
    const each = same.map((x) => `#${x.convNumber ?? '?'} (${x.status}${x.receivedAt ? `, ${dateTimeText(x.receivedAt)}` : ''})`).join(', ')
    out.push({ kind: 'sameIssue', severity: 'info', count: same.length, closeLabel: close.label, closeConvNumbers: close.convNumbers, links: Object.assign({}, ...same.map(ref)), text: `Customer also wrote about this same issue on ${each}. This reply covers ${one ? 'both' : 'all of them'}. Select ${close.label} to close ${one ? 'it' : 'them'} now.` })
  }
  const groupSecondary = Boolean(related.groupSecondary || related.duplicateOf || related.secondaryOf)
  const groupMembers = related.isPrimary ? [...dups, ...same] : []
  for (const h of groupSecondary ? [] : (related.handled ?? [])) {
    // The closed contact opens in a CXHub contact tab, read-only, and this
    // one stays open in its own tab; plain text when the viewer may not open
    // it.
    const links = h.canOpen === true ? ref(h) : {}
    const when = dateText(h.closedAt ?? h.lastReplyAt)
    const by = h.closedBy ?? 'unknown'
    const base = { kind: 'handled', severity: 'info', id: h.id, convNumber: h.convNumber, canOpen: h.canOpen === true, openLabel: `Open #${h.convNumber ?? '?'}`, links }
    if (h.answered?.covered === true) {
      // Group members about a different order than #… stay open (2026-10-21).
      const otherOrder = new Set(h.differentOrderIds ?? [])
      const close = closeTarget('handled', { selfConvNumber, members: groupMembers.filter((x) => !otherOrder.has(x.id)) })
      out.push({ ...base, offerClose: true, closeLabel: close.label, closeConvNumbers: close.convNumbers, text: `Same request as #${h.convNumber ?? '?'}, closed ${when} by ${by}. Our reply: '${h.replyExcerpt ?? ''}'. If that reply still answers this, select ${close.label}.` })
    } else if (h.answered) {
      out.push({ ...base, offerClose: false, needsReply: true, text: `We answered a similar request on #${h.convNumber ?? '?'} (closed ${when} by ${by}) before ${h.answered.changeText ?? "the customer's new message"}. This needs a new reply.` })
    } else {
      out.push({ ...base, offerClose: false, text: `Same request as #${h.convNumber ?? '?'}, closed ${when} by ${by}. Our reply: '${h.replyExcerpt ?? ''}'` })
    }
  }
  // The cancellation guard (2026-10-27): a contact on the same order whose
  // automated cancellation was held for a person, on both contacts, with
  // what the other one shows.
  for (const g of related.guardLinks ?? []) {
    if (!g || g.canOpen === false) continue
    out.push({ kind: 'guard', severity: 'warning', id: g.id, convNumber: g.convNumber, links: ref(g), text: `Same order as #${g.convNumber ?? '?'} (${g.status ?? 'unknown'}). ${g.reason ?? ''}`.trim() })
  }
  for (const d of related.differentOpen ?? []) {
    out.push({ kind: 'different', severity: 'info', id: d.id, convNumber: d.convNumber, about: aboutText(d), links: ref(d), text: `Customer also has an open contact about ${aboutText(d)}: #${d.convNumber ?? '?'}` })
  }
  return out
}

// A banner's text split into plain strings and { convNumber, id } links,
// for the component to render; each link opens its contact in a CXHub
// contact tab.
// Only a number's first mention is a link: the sentence names it again where
// it quotes a close button's label ("Select Close #7992097 …"), and that
// mention stays text (2026-10-20, button targets).
export function bannerSegments(banner) {
  const links = banner?.links ?? {}
  const linked = new Set()
  return String(banner?.text ?? '').split(/(#\d+)/).filter((part) => part !== '').map((part) => {
    const n = /^#(\d+)$/.exec(part)?.[1]
    if (!n || !links[n] || linked.has(n)) return part
    linked.add(n)
    return { convNumber: Number(n), id: links[n].id }
  })
}

export function emailTypoText(typo) {
  return `Customer email looks misspelled: ${typo.address} → probably ${typo.suggested}. Replies to the misspelled address will bounce.`
}
